import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { exportElementToPdf, pdfStamp } from "@/lib/pdf";
import { fmtEuro, fmtDate } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  BedDouble, RefreshCw, Loader2, Search, Phone, MessageCircle, Mail,
  PlaneLanding, PlaneTakeoff, Users2, CalendarClock, MapPin, ChevronRight, FileSpreadsheet, FileText, Send,
} from "lucide-react";
import { ReservationsPrint } from "@/components/ReservationsPrint";

const monthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const lastDayOf = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
};

function waLink(mobile) {
  const digits = (mobile || "").replace(/[^0-9]/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}

function MobileCell({ mobile }) {
  if (!mobile) return <span className="text-[#B9AE9A]">—</span>;
  const wa = waLink(mobile);
  return (
    <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <a href={`tel:${mobile}`} className="inline-flex items-center gap-1 text-[#45423C] hover:text-[#C86235]" data-testid="resv-call"><Phone className="h-3.5 w-3.5" /> {mobile}</a>
      {wa && <a href={wa} target="_blank" rel="noreferrer" className="text-[#3C7A3C] hover:opacity-80" title="WhatsApp" data-testid="resv-wa"><MessageCircle className="h-4 w-4" /></a>}
    </span>
  );
}

function GuestTable({ rows, kind, onSelect }) {
  const dateCol = kind === "departures" ? "tod" : "fromd";
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
            <th className="px-4 py-2.5">{kind === "departures" ? "Αναχώρηση" : "Άφιξη"}</th>
            {kind === "in_house" && <th className="px-4 py-2.5">Αναχώρηση</th>}
            <th className="px-4 py-2.5">Επισκέπτης</th>
            <th className="px-4 py-2.5">Κινητό</th>
            <th className="px-4 py-2.5">Βίλα / Δωμάτιο</th>
            <th className="px-4 py-2.5 text-center">Άτομα</th>
            <th className="px-4 py-2.5 text-right">Τζίρος</th>
            <th className="px-4 py-2.5 text-right">Υπόλοιπο</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((rv) => (
            <tr key={rv.id} data-testid={`resv-row-${rv.id}`} onClick={() => onSelect(rv)}
              className="border-t border-[#E3DED4] hover:bg-[#F7F5F1] cursor-pointer">
              <td className="px-4 py-2 tabular whitespace-nowrap">{fmtDate(rv[dateCol])}</td>
              {kind === "in_house" && <td className="px-4 py-2 tabular whitespace-nowrap">{fmtDate(rv.tod)}</td>}
              <td className="px-4 py-2 font-medium text-[#45423C]">{rv.guest_name || "—"}</td>
              <td className="px-4 py-2 whitespace-nowrap"><MobileCell mobile={rv.mobile} /></td>
              <td className="px-4 py-2 text-[#6F6A60]">{rv.room || "—"}</td>
              <td className="px-4 py-2 text-center text-[#6F6A60]">{rv.adults + rv.children}</td>
              <td className="px-4 py-2 text-right tabular" data-testid={`resv-turnover-${rv.id}`}>{fmtEuro(rv.grand_total)}</td>
              <td className={`px-4 py-2 text-right tabular font-semibold ${rv.balance > 0 ? "text-[#991B1B]" : "text-[#3C7A3C]"}`}>{fmtEuro(rv.balance)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={kind === "in_house" ? 8 : 7} className="px-4 py-8 text-center text-[#6F6A60]">Καμία κράτηση.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const FIN_ROWS = [
  ["net_total", "Καθαρή αξία (διαμονή)"],
  ["fb_total", "Εστίαση (F&B)"],
  ["extras_total", "Extras"],
  ["products_total", "Προϊόντα"],
  ["fees_total", "Χρεώσεις / Fees"],
  ["other_total", "Λοιπά"],
  ["taxes_total", "Φόροι"],
  ["vat_net_total", "ΦΠΑ διαμονής"],
  ["vat_taxes_total", "ΦΠΑ φόρων"],
  ["excluded_total", "Εξαιρούμενα"],
];

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-[#6F6A60]">{label}</p>
      <p className="text-sm text-[#45423C] mt-0.5 break-words">{children || "—"}</p>
    </div>
  );
}

function DetailDialog({ rv, onClose }) {
  const open = !!rv;
  const wa = rv ? waLink(rv.mobile) : null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="resv-detail-dialog">
        {rv && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#45423C] flex items-center gap-2">
                <BedDouble className="h-5 w-5 text-[#C86235]" /> {rv.guest_name || "Κράτηση"}
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-2 text-xs text-[#6F6A60] mt-1">
                <span className="font-mono rounded bg-[#F1EDE6] px-2 py-0.5">{rv.code || "—"}</span>
                {rv.status && <span className="rounded bg-[#F1EDE6] px-2 py-0.5">{rv.status}</span>}
                {rv.source && <span className="rounded bg-[#F1EDE6] px-2 py-0.5">{rv.source}</span>}
              </div>
              <DialogDescription className="sr-only">Λεπτομέρειες κράτησης: επικοινωνία, διαμονή και οικονομική ανάλυση.</DialogDescription>
            </DialogHeader>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-4 mt-2">
              <Field label="Κινητό">
                {rv.mobile ? (
                  <span className="inline-flex items-center gap-2" data-testid="detail-mobile">
                    <a href={`tel:${rv.mobile}`} className="inline-flex items-center gap-1 hover:text-[#C86235]"><Phone className="h-3.5 w-3.5" /> {rv.mobile}</a>
                    {wa && <a href={wa} target="_blank" rel="noreferrer" className="text-[#3C7A3C]"><MessageCircle className="h-4 w-4" /></a>}
                  </span>
                ) : null}
              </Field>
              <Field label="Σταθερό">{rv.tel}</Field>
              <Field label="Email">
                {rv.email ? <a href={`mailto:${rv.email}`} className="inline-flex items-center gap-1 hover:text-[#C86235]" data-testid="detail-email"><Mail className="h-3.5 w-3.5" /> {rv.email}</a> : null}
              </Field>
              <Field label="Χώρα / Εθνικότητα">
                {[rv.country, rv.nationality].filter(Boolean).join(" · ")}
              </Field>
              {rv.location && <Field label="Τοποθεσία"><span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {rv.location}</span></Field>}
            </div>

            {/* Stay */}
            <div className="mt-4 rounded-xl bg-[#F7F5F1] border border-[#E3DED4] p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Άφιξη">{fmtDate(rv.fromd)}</Field>
              <Field label="Αναχώρηση">{fmtDate(rv.tod)}</Field>
              <Field label="Διανυκτερεύσεις">{rv.nights_total}</Field>
              <Field label="Άτομα">{(rv.adults || 0) + (rv.children || 0)} ({rv.adults}+{rv.children})</Field>
            </div>

            {/* Accommodations */}
            {(rv.accommodations || []).length > 0 && (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wider text-[#6F6A60] mb-2">Βίλες / Δωμάτια</p>
                <div className="space-y-2">
                  {rv.accommodations.map((a, i) => (
                    <div key={i} className="rounded-lg border border-[#E3DED4] px-3 py-2 text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`detail-acc-${i}`}>
                      <div>
                        <span className="font-medium text-[#45423C]">{a.room || a.room_type || "—"}</span>
                        <span className="text-[#6F6A60]"> · {fmtDate(a.fromd)} → {fmtDate(a.tod)}</span>
                        {a.board && <span className="text-[#6F6A60]"> · {a.board}</span>}
                        {a.rate && <span className="text-[#6F6A60]"> · {a.rate}</span>}
                        {a.special_requests && <p className="text-xs text-[#C86235] mt-0.5">“{a.special_requests}”</p>}
                      </div>
                      <span className="tabular font-medium">{fmtEuro(a.grand_total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Financial breakdown */}
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-[#6F6A60] mb-2">Οικονομική ανάλυση</p>
              <div className="rounded-xl border border-[#E3DED4] overflow-hidden" data-testid="detail-financials">
                <table className="w-full text-sm">
                  <tbody>
                    {FIN_ROWS.filter(([k]) => Number(rv[k]) !== 0).map(([k, label]) => (
                      <tr key={k} className="border-b border-[#EFEBE3]">
                        <td className="px-4 py-1.5 text-[#6F6A60]">{label}</td>
                        <td className="px-4 py-1.5 text-right tabular">{fmtEuro(rv[k])}</td>
                      </tr>
                    ))}
                    <tr className="border-b border-[#EFEBE3] bg-[#F7F5F1]">
                      <td className="px-4 py-2 font-semibold text-[#45423C]">Γενικό σύνολο (τζίρος)</td>
                      <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(rv.grand_total)}</td>
                    </tr>
                    <tr className="border-b border-[#EFEBE3]">
                      <td className="px-4 py-1.5 text-[#6F6A60]">Πληρωμένα</td>
                      <td className="px-4 py-1.5 text-right tabular text-[#3C7A3C]">{fmtEuro(rv.paid_total)}</td>
                    </tr>
                    <tr className="bg-[#F7F5F1]">
                      <td className="px-4 py-2 font-semibold text-[#45423C]">Υπόλοιπο</td>
                      <td className={`px-4 py-2 text-right tabular font-semibold ${rv.balance > 0 ? "text-[#991B1B]" : "text-[#3C7A3C]"}`}>{fmtEuro(rv.balance)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TABS = [
  { key: "arrivals", label: "Αφίξεις", icon: PlaneLanding },
  { key: "departures", label: "Αναχωρήσεις", icon: PlaneTakeoff },
  { key: "in_house", label: "Διαμένουν τώρα", icon: Users2 },
];

export default function Reservations() {
  const qc = useQueryClient();
  const [rangeMode, setRangeMode] = useState("month"); // "month" | "custom"
  const [month, setMonth] = useState(monthStr());
  const [customStart, setCustomStart] = useState(`${monthStr()}-01`);
  const [customEnd, setCustomEnd] = useState(lastDayOf(monthStr()));
  const [tab, setTab] = useState("arrivals");
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const printRef = useRef(null);

  const monthRange = () => {
    const start = `${month}-01`;
    const [y, m] = month.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return [start, `${month}-${String(last).padStart(2, "0")}`];
  };
  const [start, end] = rangeMode === "month"
    ? monthRange()
    : (customStart <= customEnd ? [customStart, customEnd] : [customEnd, customStart]);

  const { data: today, isFetching: todayLoading } = useQuery({
    queryKey: ["pms-today", start, end],
    queryFn: async () => (await api.get(`/pms/today?start=${start}&end=${end}`)).data,
  });
  const { data: all, isFetching: allLoading } = useQuery({
    queryKey: ["pms-reservations", yearFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (yearFilter) params.set("year", yearFilter);
      if (search) params.set("q", search);
      const qs = params.toString();
      return (await api.get(`/pms/reservations${qs ? `?${qs}` : ""}`)).data;
    },
  });

  const sync = useMutation({
    mutationFn: async () => (await api.post("/pms/sync")).data,
    onSuccess: (d) => {
      toast.success(`Συγχρονίστηκαν ${d.synced} κρατήσεις (${d.with_mobile} με κινητό)`);
      qc.invalidateQueries({ queryKey: ["pms-today"] });
      qc.invalidateQueries({ queryKey: ["pms-reservations"] });
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const digest = useMutation({
    mutationFn: async () => (await api.post("/pms/arrivals-digest/run")).data,
    onSuccess: (d) => toast.success(`Λίστα ημέρας: ${d.arrivals} αφίξεις, ${d.departures} αναχωρήσεις · email σε ${d.emails_sent}/${d.recipients} managers${d.emails_enabled ? "" : " (email απενεργοποιημένα στο preview)"}`),
    onError: (e) => toast.error(apiErr(e)),
  });

  const exportQs = () => {
    const p = new URLSearchParams();
    if (yearFilter) p.set("year", yearFilter);
    if (search) p.set("q", search);
    return p.toString();
  };
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      await exportElementToPdf(printRef.current, `TAF_Kratiseis_${yearFilter || "ola"}_${pdfStamp()}.pdf`, "landscape", allRows.length > 500 ? 1 : 2);
    } catch (e) { toast.error("Αποτυχία PDF"); }
    setPdfBusy(false);
  };
  const allRows = all?.rows || [];
  const sumTurnover = allRows.reduce((s, r) => s + (Number(r.grand_total) || 0), 0);
  const sumBalance = allRows.reduce((s, r) => s + (Number(r.balance) || 0), 0);

  const meta = today?.meta || all?.meta;
  const tabRows = today?.[tab] || [];
  const years = all?.years || [];

  return (
    <div className="space-y-6" data-testid="reservations-page">
      <DetailDialog rv={selected} onClose={() => setSelected(null)} />
      <ReservationsPrint ref={printRef} rows={allRows} year={yearFilter} search={search} />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Hotelizer PMS</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 flex items-center gap-2"><BedDouble className="h-7 w-7 text-[#C86235]" /> Κρατήσεις</h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            Ζωντανές αφίξεις, αναχωρήσεις & διαμένοντες με όνομα, κινητό, email, βίλα και οικονομικά. Κάντε κλικ σε κράτηση για λεπτομέρειες.
            {meta?.synced_at && <span className="ml-1">Τελευταίος συγχρονισμός: <strong>{fmtDate(meta.synced_at.slice(0, 10))}</strong> · {meta.count} κρατήσεις.</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <button data-testid="resv-digest-btn" onClick={() => digest.mutate()} disabled={digest.isPending}
          className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#45423C] hover:bg-[#F7F5F1] disabled:opacity-50 whitespace-nowrap">
          {digest.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Αποστολή λίστας ημέρας
        </button>
        <button data-testid="resv-sync-btn" onClick={() => sync.mutate()} disabled={sync.isPending}
          className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50 whitespace-nowrap shrink-0">
          {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {sync.isPending ? "Συγχρονισμός…" : "Ανανέωση από Hotelizer"}
        </button>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 rounded-xl bg-[#F1EDE6] p-1">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`resv-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${tab === t.key ? "bg-white text-[#45423C] shadow-sm" : "text-[#6F6A60] hover:text-[#45423C]"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
                <span className="ml-1 rounded-full bg-[#E3DED4] px-1.5 text-[11px]">{(today?.[t.key] || []).length}</span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-xl bg-[#F1EDE6] p-1">
              <button data-testid="resv-mode-month" onClick={() => setRangeMode("month")}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${rangeMode === "month" ? "bg-white text-[#45423C] shadow-sm" : "text-[#6F6A60]"}`}>Μήνας</button>
              <button data-testid="resv-mode-custom" onClick={() => setRangeMode("custom")}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${rangeMode === "custom" ? "bg-white text-[#45423C] shadow-sm" : "text-[#6F6A60]"}`}>Εύρος</button>
            </div>
            <CalendarClock className="h-4 w-4 text-[#6F6A60]" />
            {rangeMode === "month" ? (
              <input data-testid="resv-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]" />
            ) : (
              <div className="flex items-center gap-1">
                <input data-testid="resv-date-from" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                  className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]" />
                <ChevronRight className="h-4 w-4 text-[#6F6A60]" />
                <input data-testid="resv-date-to" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                  className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]" />
              </div>
            )}
          </div>
        </div>
        {todayLoading && !today ? (
          <div className="px-5 py-10 text-center text-sm text-[#6F6A60] flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Φόρτωση…</div>
        ) : (
          <GuestTable rows={tabRows} kind={tab} onSelect={setSelected} />
        )}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-medium text-[#45423C]">Όλες οι κρατήσεις {all ? `(${allRows.length})` : ""}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="resv-export-xlsx" onClick={() => downloadXlsx(`/pms/reservations/export.xlsx?${exportQs()}`, `TAF_Kratiseis_${yearFilter || "ola"}.xlsx`)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm font-semibold text-[#3C7A3C] hover:bg-[#F7F5F1]">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button data-testid="resv-export-pdf" onClick={exportPdf} disabled={pdfBusy || !allRows.length}
              className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm font-semibold text-[#991B1B] hover:bg-[#F7F5F1] disabled:opacity-50">
              {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
            </button>
            <select data-testid="resv-year" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}
              className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]">
              <option value="">Όλα τα έτη</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <div className="relative">
              <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="resv-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Αναζήτηση όνομα / κωδικό / κινητό / email"
                className="rounded-xl border border-[#E3DED4] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#45423C] w-full sm:w-80" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-2.5">Άφιξη</th><th className="px-4 py-2.5">Αναχώρηση</th>
                <th className="px-4 py-2.5">Επισκέπτης</th><th className="px-4 py-2.5">Κινητό</th>
                <th className="px-4 py-2.5">Βίλα</th><th className="px-4 py-2.5">Κατάσταση</th>
                <th className="px-4 py-2.5 text-right">Τζίρος</th><th className="px-4 py-2.5 text-right">Υπόλοιπο</th>
              </tr>
            </thead>
            <tbody>
              {allRows.map((rv) => (
                <tr key={rv.id} data-testid={`resv-all-${rv.id}`} onClick={() => setSelected(rv)}
                  className="border-t border-[#E3DED4] hover:bg-[#F7F5F1] cursor-pointer">
                  <td className="px-4 py-2 tabular whitespace-nowrap">{fmtDate(rv.fromd)}</td>
                  <td className="px-4 py-2 tabular whitespace-nowrap">{fmtDate(rv.tod)}</td>
                  <td className="px-4 py-2 font-medium text-[#45423C]">{rv.guest_name || "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap"><MobileCell mobile={rv.mobile} /></td>
                  <td className="px-4 py-2 text-[#6F6A60]">{rv.room || "—"}</td>
                  <td className="px-4 py-2 text-xs text-[#6F6A60]">{rv.status || "—"}</td>
                  <td className="px-4 py-2 text-right tabular" data-testid={`resv-all-turnover-${rv.id}`}>{fmtEuro(rv.grand_total)}</td>
                  <td className={`px-4 py-2 text-right tabular font-semibold ${rv.balance > 0 ? "text-[#991B1B]" : "text-[#3C7A3C]"}`}>{fmtEuro(rv.balance)}</td>
                </tr>
              ))}
              {all && allRows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6F6A60]">{allLoading ? "Φόρτωση…" : "Καμία κράτηση. Πατήστε «Ανανέωση από Hotelizer» για συγχρονισμό."}</td></tr>}
            </tbody>
            {allRows.length > 0 && (
              <tfoot>
                <tr className="bg-[#45423C] text-white font-semibold" data-testid="resv-all-totals">
                  <td className="px-4 py-2.5" colSpan={6}>ΣΥΝΟΛΟ {allRows.length} κρατήσεων</td>
                  <td className="px-4 py-2.5 text-right tabular" data-testid="resv-total-turnover">{fmtEuro(sumTurnover)}</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(sumBalance)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
